import React, { lazy, Suspense, useEffect } from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import DashboardLayout from "./layouts/dashboard";
// fonts
import "./assets/fonts/mod-aber-mono-bold-webfont.woff";
import "./assets/fonts/mod-aber-mono-bold-webfont.woff2";
import "./assets/fonts/mod-aber-mono-light-webfont.woff";
import "./assets/fonts/mod-aber-mono-light-webfont.woff2";
import "./assets/fonts/mod-aber-mono-regular-webfont.woff";
import "./assets/fonts/mod-aber-mono-regular-webfont.woff2";

import AuthLayout from "./layouts/auth";
// guards
import GuardedRoute from "./guards/GuardedRoute";
import UnGuardedRoute from "./guards/UnGuardedRoute";
import ClipLoader from "react-spinners/ClipLoader";
import moment from "moment-timezone";
import { useWalletConnector, useKeplrWalletConnector } from "foundry";
import { PATH_DASHBOARD } from "./routes/paths";
import {
  REFRESH_TOKEN_TIMER,
  IS_MULTISWAP,
  TOKEN_TAG,
} from "./utils/const.utils";
import { useDispatch } from "react-redux";
import { getPhraseDataDispatch } from "./redux/slices/phrase";
import { useSelector } from "react-redux";
import { WalletAuthencationOnSignIn } from "./components/common/wallet-authentication/WalletAuthenticationSignIn";
import { RootState } from "./redux/rootReducer";
import { cFRMBNBModule } from "./components/crucible/farms";
import * as ConfigActions from "./redux/slices/config";
import * as SwapActions from "./redux/slices/swap";
import VestingDashboardPage from "./components/vesting/user/VestingDashboardPage";
import VestingAdminDashboard from "./components/vesting/admin";
import VestingForm from "./components/vesting/vesting-information/vesting-forms";
import VestingContainer from "./components/vesting/admin/vesting-container";
import VestingInformationTable from "./components/vesting/vesting-information/vesting-information-table";
import Loader from "./assets/gif/animation-loading.gif";
import * as walletAuthenticationActions from "./components/common/wallet-authentication/redux/walletAuthenticationActions";

const Loadable = (Component: any) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  // const { pathname } = useLocation();
  // const isDashboard = pathname.includes("/dashboard");
  return (
    <Suspense
      fallback={
        <img
          src={Loader}
          className="loader-center"
          width={120}
          height={120}
          alt="loading..."
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

const Lander = Loadable(
  lazy(() => import("./components/bridge/pages/lander/Lander"))
);
const LeaderboardById = Loadable(
  lazy(() => import("./components/leaderboard/LeaderboardInformation"))
);
const CreateLeaderboard = Loadable(
  lazy(() => import("./components/leaderboard/NewLeaderboard"))
);
const MultiTokenLeaderboardById = Loadable(
  lazy(() => import("./components/leaderboard-multitoken/index"))
);
const LeaderboardManagement = Loadable(
  lazy(() => import("./components/leaderboard/LeaderboardManagement"))
);
// const CommunityRegister = Loadable(lazy(() => import("./components/authentication/community/register")));
// const CommunityLogin = Loadable(lazy(() => import("./components/authentication/community/login")));
const OrganizationRegister = Loadable(
  lazy(() => import("./components/authentication/organization/register/index"))
);
const OrganizationLogin = Loadable(
  lazy(() => import("./components/authentication/organization/login"))
);
const CreateCompetition = Loadable(
  lazy(() => import("./components/competition/NewCompetition"))
);
const CompetitionManagement = Loadable(
  lazy(() => import("./components/competition/CompetitionManagement"))
);
const CompetitionById = Loadable(
  lazy(() => import("./components/competition/CompetitionInformation"))
);
const Page404 = Loadable(lazy(() => import("./components/error/page404")));
const ForgotPassword = Loadable(
  lazy(() => import("./components/common/forgot-password/index"))
);
const ResetPassword = Loadable(
  lazy(() => import("./components/common/reset-password/index"))
);
const WalletAuthentication = Loadable(
  lazy(() => import("./components/common/wallet-authentication/index"))
);
const EmailVerification = Loadable(
  lazy(() => import("./components/common/email-verification/index"))
);
const ResendEmailVerification = Loadable(
  lazy(() => import("./components/common/resend-email-verification/index"))
);
const StakingLeaderboard = Loadable(
  lazy(() => import("./components/leaderboard-staking/StakingLeaderboard"))
);
const Dashboard = Loadable(
  lazy(() => import("./components/dashboard/dashboard"))
);
const ProfileSettings = Loadable(
  lazy(() => import("./components/profile-management"))
);
const CrucibleDashboardPage = Loadable(
  lazy(() => import("./components/crucible/dashboard/CrucibleDashboardPage"))
);
const CruciblePublic = Loadable(
  lazy(() => import("./components/crucible/public/Introduction"))
);

function App() {
  const dispatch = useDispatch();
  const isAuthenticated = localStorage.getItem(TOKEN_TAG);
  const { versionHistory } = JSON.parse(
    localStorage.getItem("persist:swap") || "{}"
  );
  const { isKeplrConnected } = useKeplrWalletConnector();
  const { isConnected, web3Sdk, walletAddress } = useWalletConnector();
  const { isThemeLoading } = useSelector((state: RootState) => state.config);

  moment.tz.setDefault("UTC");
  useEffect(() => {
    if (!IS_MULTISWAP) {
      dispatch(ConfigActions.loadWhiteLabelConfig(window.location.host));
    }
    dispatch(SwapActions.getVersionHistory(versionHistory));
    dispatch(getPhraseDataDispatch());
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isConnected || isKeplrConnected) {
      const interval = setInterval(() => {
        getRefreshToken();
      }, REFRESH_TOKEN_TIMER);

      return () => clearInterval(interval);
    }
  }, [isConnected, isKeplrConnected]);

  const getRefreshToken = () => {
    let oldToken = localStorage.getItem(TOKEN_TAG);
    if (oldToken) {
      walletAuthenticationActions.refreshTokenAndStore(oldToken);
      // toast.error("Token expired Please connect your wallet again");
      // dispatch(walletAuthenticationActions.removeSessions());
      // if (isConnected) {
      //   walletConnectionHandler(currentWallet);
      // } else if (isKeplrConnected) keplrWalletDisconnectionHandler();
    }
  };

  if (isThemeLoading) {
    return <ClipLoader loading={true} size={150} />;
  }

  return (
    <>
      <Switch>
        <Route exact path="/">
          {" "}
          {IS_MULTISWAP ? (
            <Redirect to={`${PATH_DASHBOARD.multiswap.swap}`} />
          ) : (
            <>
              <Redirect to={`${PATH_DASHBOARD.crucible.index}`} />
            </>
            // ) : (
            //   <>
            //     <Redirect
            // to={`${PATH_DASHBOARD.crucible.index}`}
            // .
            //       to={`${PATH_PUBLIC_USER.competition.root}/62a213274cc654248b68a8da`}
            //     />{" "}
            //     <Redirect
            //       to={`/pub/staking/leaderboard/62a1f76a8e831b2368e1088e`}
            //     />{" "}
            //   </>
          )}
        </Route>
        <GuardedRoute
          path={PATH_DASHBOARD.crucible.crucibleActionRoutes.manage}
          component={cFRMBNBModule.cFRMBNBFarmingDashboardFlowManage}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="Crucible"
        />
        <GuardedRoute
          path={PATH_DASHBOARD.crucible.crucibleActionRoutes.introduction}
          component={cFRMBNBModule.cFRMBNBFarmingDashboardFlowIntroduction}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="Crucible"
        />
        <GuardedRoute
          path={PATH_DASHBOARD.crucible.crucibleActionRoutes.mint.mint}
          component={cFRMBNBModule.cFRMBNBFarmingMintFlowMint}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="Crucible"
        />
        <GuardedRoute
          path={PATH_DASHBOARD.crucible.crucibleActionRoutes.mint.success}
          component={cFRMBNBModule.cFRMBNBFarmingMintFlowSuccess}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="Crucible"
        />
        <GuardedRoute
          path={
            PATH_DASHBOARD.crucible.crucibleActionRoutes.mintAndStake
              .mintAndStake
          }
          component={cFRMBNBModule.cFRMBNBFarmingStakeFlowMintAndStake}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="Crucible"
        />
        <GuardedRoute
          path={
            PATH_DASHBOARD.crucible.crucibleActionRoutes.mintAndStake
              .introduction
          }
          component={cFRMBNBModule.cFRMBNBFarmingDashboardFlowIntroduction}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="Crucible"
        />
        <GuardedRoute
          path={PATH_DASHBOARD.crucible.crucibleActionRoutes.mintAndStake.steps}
          component={cFRMBNBModule.cFRMBNBFarmingDashboardFlowIntroduction}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="Crucible"
        />
        <GuardedRoute
          path={
            PATH_DASHBOARD.crucible.crucibleActionRoutes.mintAndStake.success
          }
          component={cFRMBNBModule.cFRMBNBFarmingStakingMintFlowSuccess}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="Crucible"
        />
        <GuardedRoute
          path={PATH_DASHBOARD.crucible.crucibleActionRoutes.stake.success}
          component={cFRMBNBModule.cFRMBNBFarmingStakeFlowSucess}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="Crucible"
        />
        <GuardedRoute
          path={PATH_DASHBOARD.crucible.crucibleActionRoutes.stake.stake}
          component={cFRMBNBModule.cFRMBNBFarmingStakeFlowStake}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="Crucible"
        />
        <GuardedRoute
          path={PATH_DASHBOARD.crucible.crucibleActionRoutes.unstake.success}
          component={cFRMBNBModule.cFRMBNBFarmingUnStakeFlowSucess}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="Crucible"
        />
        <GuardedRoute
          path={PATH_DASHBOARD.crucible.crucibleActionRoutes.unstake.unstake}
          component={cFRMBNBModule.cFRMBNBFarmingUnStakeFlowUnStake}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="Crucible"
        />
        <GuardedRoute
          path={
            PATH_DASHBOARD.crucible.crucibleActionRoutes.unstake.addLiquidity
          }
          component={cFRMBNBModule.cFRMBNBFarmingUnStakeFlowAddLiquidity}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="Crucible"
        />
        <GuardedRoute
          path={
            PATH_DASHBOARD.crucible.crucibleActionRoutes.unstake.removeLiquidity
          }
          component={cFRMBNBModule.cFRMBNBFarmingUnStakeFlowRemoveLiquidity}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="Crucible"
        />
        <GuardedRoute
          path={PATH_DASHBOARD.crucible.crucibleActionRoutes.liquidity}
          component={cFRMBNBModule.cFRMBNBFarmingLiquidityFlow}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="Crucible"
        />
        <GuardedRoute
          path={PATH_DASHBOARD.crucible.crucibleActionRoutes.withdraw.success}
          component={cFRMBNBModule.cFRMBNBFarmingWithdrawFlowSucess}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="Crucible"
        />
        <GuardedRoute
          path={PATH_DASHBOARD.crucible.crucibleActionRoutes.withdraw.withdraw}
          component={cFRMBNBModule.cFRMBNBFarmingWithdrawFlowWithdraw}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="Crucible"
        />
        <GuardedRoute
          path={PATH_DASHBOARD.crucible.crucibleActionRoutes.withdraw.steps}
          component={cFRMBNBModule.cFRMBNBFarmingWithdrawFlowSteps}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="Crucible"
        />
        <GuardedRoute
          path={PATH_DASHBOARD.crucible.crucibleActionRoutes.unwrap.unwrap}
          component={cFRMBNBModule.cFRMBNBFarmingUnWrapFlowUnWrap}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="Crucible"
        />
        <GuardedRoute
          path={PATH_DASHBOARD.crucible.crucibleActionRoutes.unwrap.success}
          component={cFRMBNBModule.cFRMBNBFarmingUnWrapFlowSuccess}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="Crucible"
        />
        <GuardedRoute
          path={PATH_DASHBOARD.vesting.adminDashboard}
          component={VestingAdminDashboard}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="Vesting"
        />
        <GuardedRoute
          path={PATH_DASHBOARD.vesting.general}
          component={VestingContainer}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="Vesting"
        />
        <GuardedRoute
          path={PATH_DASHBOARD.vesting.form}
          component={VestingForm}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="Vesting"
        />
        <GuardedRoute
          path={PATH_DASHBOARD.vesting.card}
          component={VestingInformationTable}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="Vesting"
        />
        {/* <UnGuardedRoute path={PATH_DASHBOARD.crucible.public} component={CruciblePublic} auth={isAuthenticated} layout={DashboardLayout} headerTitle="Crucible" /> */}

        <UnGuardedRoute
          path="/home"
          component={Dashboard}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle=""
        />
        <UnGuardedRoute
          path="/auth/forgot-password"
          component={ForgotPassword}
          auth={isAuthenticated}
          layout={AuthLayout}
          headerTitle=""
        />
        <UnGuardedRoute
          path="/auth/verify"
          component={EmailVerification}
          auth={isAuthenticated}
          layout={AuthLayout}
          headerTitle=""
        />
        <UnGuardedRoute
          path="/auth/resend-code"
          component={ResendEmailVerification}
          auth={isAuthenticated}
          layout={AuthLayout}
          headerTitle=""
        />
        <UnGuardedRoute
          path="/auth/reset-password/:token"
          component={ResetPassword}
          auth={isAuthenticated}
          layout={AuthLayout}
          headerTitle=""
        />
        <UnGuardedRoute
          path="/auth/wallet-authentication"
          component={WalletAuthentication}
          auth={isAuthenticated}
          layout={AuthLayout}
          headerTitle=""
        />
        {/* <UnGuardedRoute path='/auth/login' component={CommunityLogin} auth={isAuthenticated} layout={AuthLayout}/>
          <UnGuardedRoute path='/auth/register' component={CommunityRegister} auth={isAuthenticated} layout={AuthLayout}/> */}
        <UnGuardedRoute
          path="/auth/org/register"
          component={OrganizationRegister}
          auth={isAuthenticated}
          layout={AuthLayout}
          headerTitle=""
        />
        <UnGuardedRoute
          path="/auth/org/login"
          component={OrganizationLogin}
          auth={isAuthenticated}
          layout={AuthLayout}
          headerTitle=""
        />
        <UnGuardedRoute
          path="/pub/leaderboard/:id"
          component={LeaderboardById}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle=""
        />
        <UnGuardedRoute
          path="/pub/multi/leaderboard/:id"
          component={MultiTokenLeaderboardById}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle=""
        />
        <UnGuardedRoute
          path="/pub/competition/:id"
          component={CompetitionById}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle=""
        />
        <UnGuardedRoute
          path="/pub/staking/leaderboard/:id"
          component={StakingLeaderboard}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle=""
        />
        <GuardedRoute
          path="/dashboard/staking/leaderboard/:id"
          component={StakingLeaderboard}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle=""
        />
        <GuardedRoute
          path="/dashboard/leaderboard/management"
          component={LeaderboardManagement}
          auth={isAuthenticated}
          layout={DashboardLayout}
        />
        <GuardedRoute
          path="/dashboard/leaderboard/create"
          component={CreateLeaderboard}
          auth={isAuthenticated}
          layout={DashboardLayout}
        />
        <GuardedRoute
          path="/dashboard/competition/create"
          component={CreateCompetition}
          auth={isAuthenticated}
          layout={DashboardLayout}
        />
        <GuardedRoute
          path="/dashboard/competition/management"
          component={CompetitionManagement}
          auth={isAuthenticated}
          layout={DashboardLayout}
        />
        <GuardedRoute
          path="/dashboard/competition/:id"
          component={CompetitionById}
          auth={isAuthenticated}
          layout={DashboardLayout}
        />
        <GuardedRoute
          path="/dashboard/multi/leaderboard/:id"
          component={MultiTokenLeaderboardById}
          auth={isAuthenticated}
          layout={DashboardLayout}
        />
        <GuardedRoute
          path="/dashboard/leaderboard/:id"
          component={LeaderboardById}
          auth={isAuthenticated}
          layout={DashboardLayout}
        />
        <UnGuardedRoute
          path={PATH_DASHBOARD.general.profile}
          component={ProfileSettings}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="My Profile"
        />
        <UnGuardedRoute
          path="/"
          component={Lander}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle=""
        />
        <UnGuardedRoute
          path={PATH_DASHBOARD.general.profile}
          component={ProfileSettings}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="My Profile"
        />
        {/* <UnGuardedRoute path={PATH_DASHBOARD.crucible.deployer} component={CrucibleDeployer} auth={isAuthenticated} layout={DashboardLayout} headerTitle="Crucible" /> */}
        <UnGuardedRoute
          path={PATH_DASHBOARD.crucible.index}
          component={CrucibleDashboardPage}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="CRUCIBLE"
        />
        <UnGuardedRoute
          path={PATH_DASHBOARD.vesting.index}
          component={VestingDashboardPage}
          auth={isAuthenticated}
          layout={DashboardLayout}
          headerTitle="Vesting"
        />

        <Route path="*" component={Page404}></Route>
      </Switch>
      <WalletAuthencationOnSignIn account={walletAddress} web3Sdk={web3Sdk} />
    </>
  );
}

export default App;

///pub/multi/leaderboard/61b6d48337f5125acbbfddeb
